import React, { Component } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import 'moment-timezone';
import { compose, graphql } from 'react-apollo';
import { SUBSCRIPTION_PREFERRED_LOCATION_CHANGED } from 'containers/DesktopNotificationTray';
import LoadingIndicator from 'components/LoadingIndicator';
import TextButton from 'components/TextButton';
import { formatNumber } from 'utils/formatting';
import { getDateTime } from '../common';
import Prize from '../components/Prize';
import { QUERY_GET_AUCTIONS, SUBSCRIPTION_AUCTION_EVENT } from '../graphql';
import './Completed.css';

const ITEMS_PER_PAGE = 20;
class AuctionsCompleted extends Component {
  state = {
    page: 1
  };

  componentDidMount() {
    const { auctionsData } = this.props;
    this.unsubscribe = auctionsData.subscribe();
    this.unsubscribeFromLocationData = auctionsData.subscribeToLocationData();
  }

  componentWillUnmount() {
    this.unsubscribe();
    this.unsubscribeFromLocationData = () => {};
  }

  pageRef = React.createRef();
  pageTopRef = React.createRef();

  handleLoadMore = async () => {
    const { auctionsData } = this.props;
    const { page } = this.state;
    this.setState({ isLoading: true, page: page + 1 });
    await auctionsData.loadMore({ page: page + 1 });
    this.setState({ isLoading: false });
  };

  render() {
    const { auctionsData } = this.props;
    const { isLoading } = this.state;

    const { loading, data } = auctionsData;
    const auctionsSorted = [...data].sort((a, b) =>
      getDateTime(b.endTime).diff(getDateTime(a.endTime))
    );

    return (
      <div className="auctions-completed" ref={this.pageRef}>
        {auctionsSorted.map(item => (
          <Auction
            key={item.id}
            title={item.name}
            description={item.description}
            prizes={item.prizes.prizes}
            endTime={item.endTime}
            winner={item.winner && item.winner.user.displayName}
            bid={item.winner && item.winner.bid}
          />
        ))}
        {auctionsSorted.length ? (
          <TextButton
            className="auctions-completed-more"
            disabled={loading || isLoading}
            onClick={this.handleLoadMore}
          >
            Load More ...
          </TextButton>
        ) : (
          <div className="empty-auctions-message">
            <span className="auctions-message-header">No Past Auctions</span>
          </div>
        )}
        {loading && <LoadingIndicator overlay />}
      </div>
    );
  }
}

const Auction = ({ title, description, prizes, endTime, winner, bid }) => {
  return (
    <div className="auction">
      <div className="auction-img">
        <Prize prizes={prizes} />
      </div>
      <div className="auction-body-wrapper">
        <div className="auction-body">
          <h2 className="auction-title">{title}</h2>
          <div className="auction-description">{description}</div>
          <span className="auction-date">
            Ended:{' '}
            {`${getDateTime(endTime).format('MM/DD/YYYY, hh:mm A')} ${moment
              .tz(moment.tz.guess())
              .format('z')}`}
          </span>
          <div className="auction-winner">
            {winner && (
              <div className="auction-winner-name">
                <div className="auction-winner-title">Winner:</div>
                <div className="auction-winner-value notranslate">{winner}</div>
              </div>
            )}
            {bid && (
              <div className="auction-winner-bid">
                <div className="auction-winner-title">Winning Bid:</div>
                <div className="auction-winner-value">{formatNumber(bid)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AuctionsCompletedConnected = compose(
  graphql(QUERY_GET_AUCTIONS, {
    name: 'auctionsData',
    options: props => ({
      variables: {
        input: {
          status: 'Completed',
          sort: 'DESC',
          limit: ITEMS_PER_PAGE
        }
      },
      fetchPolicy: 'network-only'
    }),
    props: data => {
      const response = data.auctionsData;
      return {
        auctionsData: {
          ...response,
          data: response.auctions ? response.auctions.auctions : [],
          subscribe: () => {
            return response.subscribeToMore({
              document: SUBSCRIPTION_AUCTION_EVENT,
              updateQuery: (prev, { subscriptionData: { data } }) => {
                const { event, id, ...rest } = data;
                let result = cloneDeep(prev);

                if (event === 'finalize') {
                  result.auctions.auctions.push({
                    ...rest,
                    __typename: 'Auction',
                    id,
                    status: 'Completed'
                  });
                }

                return result;
              }
            });
          },
          subscribeToLocationData: () => {
            return response.subscribeToMore({
              document: SUBSCRIPTION_PREFERRED_LOCATION_CHANGED,
              updateQuery: () => response.refetch()
            });
          },
          loadMore: ({ page }) => {
            return response.fetchMore({
              variables: {
                input: {
                  status: 'Completed',
                  sort: 'DESC',
                  offset: (page - 1) * ITEMS_PER_PAGE,
                  limit: ITEMS_PER_PAGE
                }
              },
              updateQuery: (prev, { fetchMoreResult, queryVariables }) => {
                const result = cloneDeep(prev);
                result.auctions.auctions = [
                  ...result.auctions.auctions,
                  ...fetchMoreResult.auctions.auctions
                ];

                return result;
              }
            });
          }
        }
      };
    }
  })
)(AuctionsCompleted);

export default AuctionsCompletedConnected;
